import React, { useCallback, useState } from 'react';

import { Button } from '~/components/button';
import { Color } from '~/components/color';
import { CloseIcon } from '~/components/icon';
import { FormattedMessage, useIntl } from '~/contexts/intl';
import { CANCELLATION_TERMS_URL, openUrl, renderLink } from '~/utils/open-url';

import { AppointmentActionTitle, AppointmentActionDescription } from './appointment-actions';

export type AppointmentCancelProps = {
    cancellable: boolean;
    onCancelCalendarEvent: () => Promise<void>;
};

export function AppointmentCancel(props: AppointmentCancelProps) {
    const { cancellable, onCancelCalendarEvent } = props;
    const [loading, setLoading] = useState<boolean>(false);
    const { formatMessage } = useIntl();

    const handleCancelCalendarEvent = useCallback(async () => {
        setLoading(true);
        try {
            await onCancelCalendarEvent();
        } catch (error) {
            // Re-enable only on failure — on success the modal is closing and the button
            // must stay disabled so it can't re-fire during the hide animation.
            setLoading(false);
            throw error;
        }
    }, [onCancelCalendarEvent]);

    const handleLink = useCallback(async () => {
        await openUrl(CANCELLATION_TERMS_URL);
    }, []);

    return (
        <>
            <AppointmentActionTitle>
                <FormattedMessage id="appointment.cancel.title" values={{ cancellable }} />
            </AppointmentActionTitle>
            <AppointmentActionDescription>
                <FormattedMessage
                    id="appointment.cancel.description"
                    values={{
                        link: renderLink(handleLink),
                        cancellable
                    }}
                />
            </AppointmentActionDescription>
            {cancellable ? (
                <Button
                    alignCenter
                    fillContainer
                    loading={loading}
                    type="negative"
                    shape="rounded"
                    leftIcon={<CloseIcon fill={Color.ICON_NEGATIVE} />}
                    label={formatMessage('appointment.cancel')}
                    onPress={handleCancelCalendarEvent}
                />
            ) : null}
        </>
    );
}
